/**
 * DASHBOARD-RPS
 * Burnout indicators
 */

import { useEffect, useState } from "react"
import NavDashboard from "@/nav/nav.dashboard"
import UpgradeWidget from "@/widgets/upgrade.widget"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChartColumn, faList, faPeopleGroup } from "@fortawesome/free-solid-svg-icons"
import ListItem from "@/components/list-item"
import DashboardRpsOverviewWidget from "@/widgets/dashboard-rps-overview.widget"
import DashboardRpsBottomSkeletonWidget from "@/widgets/skeletons/dashboard-rps-bottom-skeleton.widget"
import DashboardRpsTeamsWidget from "@/widgets/dashboard-rps-teams.widget"
import DashboardRpsQuestionsWidget from "@/widgets/dashboard-rps-questions.widget"
import DashboardRpsUsersWidget from "@/widgets/dashboard-rps-users.widget"
import { DashboardRpsGollacWidget } from "@/widgets/dashboard-rps-gollac.widget"
import { useSelector } from "react-redux"
import { selectSession } from "@/redux/_session.selector"
import { selectFilter } from "@/redux/filter.selectors"
import { selectSurvey } from "@/redux/survey.selectors"
import DashboardRpsGollacOverwiewWidget from "@/widgets/dashboard-rps-gollac-overwiew.widget"
import { DashboardRpsGollacTeamsWidget } from "@/widgets/dashboard-rps-gollac-teams.widget"
import { t } from "i18next"
import DashboardRpsGollacQuestionsWidget from "@/widgets/dashboard-rps-gollac-questions.widget"
import PagePlaceholder from "@/components/page-placeholder"
import { AnimatePresence, motion } from "framer-motion"
import { useAnimationsDisabled } from "@/utils/animation-mode.utils"

const TAB_QUESTIONS_KARASEK = "TAB_QUESTIONS_KARASEK"
const TAB_TEAMS_KARASEK = "TAB_TEAMS_KARASEK"
const TAB_USERS_KARASEK = "TAB_USERS_KARASEK"

const TAB_QUESTIONS_GOLLAC = "TAB_QUESTIONS_GOLLAC"
const TAB_TEAMS_GOLLAC = "TAB_TEAMS_GOLLAC"
const TAB_STEPS_GOLLAC = "TAB_STEPS_GOLLAC"

const DashboardRps = () => {
  // Redux selectors
  const _session = useSelector(selectSession)
  const filter = useSelector(selectFilter)
  const survey = useSelector(selectSurvey)
  const [gollacHasData, setGollacHasData] = useState<boolean | null>(null)

  // State management with hooks
  const isGollac = survey.active.template.options.isGollac
  const [currentTab, setCurrentTab] = useState(isGollac ? TAB_TEAMS_GOLLAC : TAB_TEAMS_KARASEK)
  const [isOverviewLoading, setIsOverviewLoading] = useState(true)

  //Transition entre les onglets : fondu et léger glissement (coupée avec « réduire les animations »)
  const isAnimationsDisabled = useAnimationsDisabled()
  const tabTransition = isAnimationsDisabled
    ? {}
    : {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -6 },
        transition: { duration: 0.18 }
      }

  // const
  const tabsListKarasek = [
    {
      id: "item-1",
      icon: faPeopleGroup,
      name: t("rps_teams_title"),
      currentTab: TAB_TEAMS_KARASEK
    },
    {
      id: "item-2",
      icon: faList,
      name: t("rps_questions_title"),
      currentTab: TAB_QUESTIONS_KARASEK
    },
    {
      id: "item-3",
      icon: faChartColumn,
      name: t("rps_users_title"),
      currentTab: TAB_USERS_KARASEK
    }
  ]

  const tabsListGollac = [
    {
      id: "item-1",
      icon: faPeopleGroup,
      name: t("rps_teams_title"),
      currentTab: TAB_TEAMS_GOLLAC
    },
    {
      id: "item-2",
      icon: faList,
      name: t("dashboard_compare_simple"),
      currentTab: TAB_QUESTIONS_GOLLAC
    },
    {
      id: "item-3",
      icon: faChartColumn,
      name: t("rps_users_gap"),
      currentTab: TAB_STEPS_GOLLAC
    }
  ]

  useEffect(() => {
    setCurrentTab(isGollac ? TAB_TEAMS_GOLLAC : TAB_TEAMS_KARASEK)
  }, [isGollac])

  // render
  return (
    <NavDashboard
      title="rps_title"
      hideFilters={!_session.modules.survey.rps || gollacHasData === false}
      hideOptions={!_session.modules.survey.rps || gollacHasData === false}
      helpKey={isGollac ? "dashboard.rps-gollac" : "dashboard.rps"}>
      {!_session.modules.survey.rps ? (
        <UpgradeWidget feature="rps" />
      ) : (
        <div>
          {!isGollac && (
            <>
              <DashboardRpsOverviewWidget dashboardFilters={filter.dashboard} onLoadingChange={setIsOverviewLoading} />
              {isOverviewLoading && <DashboardRpsBottomSkeletonWidget />}
              {!isOverviewLoading && survey.active.rps.total > 0 && (
                <div className="flex">
                  <div
                    className="flex flex-dcol"
                    style={{
                      marginRight: "1rem",
                      height: "50%"
                    }}>
                    {tabsListKarasek.map((item) => {
                      const isActive = currentTab === item.currentTab
                      return (
                        <div
                          className="cursor-pointer"
                          key={item.id}
                          onClick={() => setCurrentTab(item.currentTab)}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F0F0F0")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
                          <ListItem>
                            {isActive && (
                              <motion.div
                                layoutId="rps-tab-indicator"
                                className="width-10 abs"
                                style={{ backgroundColor: _session.accountColors.active, height: "100%", top: 0, left: 0 }}
                                transition={{ type: "spring", stiffness: 500, damping: 40 }}
                              />
                            )}
                            <div className={`${isActive ? "dark-t" : "grey-t"} flex flex-col justify-center w-full`}>
                              <FontAwesomeIcon icon={item.icon} className="p-1" />
                              <div className="text-center" style={{ fontSize: 12 }}>
                                {item.name}
                              </div>
                            </div>
                          </ListItem>
                        </div>
                      )
                    })}
                  </div>

                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div key={currentTab} className="flex1" style={{ minWidth: 0 }} {...tabTransition}>
                      {currentTab === TAB_TEAMS_KARASEK && <DashboardRpsTeamsWidget />}

                      {currentTab === TAB_QUESTIONS_KARASEK && (
                        <DashboardRpsQuestionsWidget dashboardFilters={filter.dashboard} />
                      )}

                      {currentTab === TAB_USERS_KARASEK && (
                        <DashboardRpsUsersWidget dashboardFilters={filter.dashboard} />
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}
            </>
          )}

          {isGollac && (
            <>
              {gollacHasData === false ? (
                <PagePlaceholder
                  image="sherlock"
                  title={t("rps_gollac_no_questions_title")}
                  help={t("rps_gollac_no_questions_help")}
                />
              ) : (
                <>
                  <DashboardRpsGollacOverwiewWidget onLoad={setGollacHasData} />
                  {gollacHasData === true && <div className="flex">
                    <div className="flex flex-col mr-4 shrink-0">
                      {tabsListGollac.map((item) => {
                        const isActive = currentTab === item.currentTab
                        return (
                          <div
                            className="cursor-pointer"
                            key={item.id}
                            onClick={() => setCurrentTab(item.currentTab)}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F0F0F0")}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
                            <ListItem>
                              {isActive && (
                                <motion.div
                                  layoutId="rps-tab-indicator"
                                  className="width-10 abs"
                                  style={{ backgroundColor: _session.accountColors.active, height: "100%", top: 0, left: 0 }}
                                  transition={{ type: "spring", stiffness: 500, damping: 40 }}
                                />
                              )}
                              <div className={`${isActive ? "dark-t" : "grey-t"} flex flex-col justify-center w-full`}>
                                <FontAwesomeIcon icon={item.icon} className="p-1" />
                                <div className="text-center" style={{ fontSize: 12 }}>
                                  {item.name}
                                </div>
                              </div>
                            </ListItem>
                          </div>
                        )
                      })}
                    </div>

                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div key={currentTab} className="flex1" style={{ minWidth: 0 }} {...tabTransition}>
                        {currentTab === TAB_TEAMS_GOLLAC && <DashboardRpsGollacTeamsWidget />}

                        {currentTab === TAB_QUESTIONS_GOLLAC && (
                          <DashboardRpsGollacQuestionsWidget dashboardFilters={filter.dashboard} />
                        )}

                        {currentTab === TAB_STEPS_GOLLAC && <DashboardRpsGollacWidget />}
                      </motion.div>
                    </AnimatePresence>
                  </div>}
                </>
              )}
            </>
          )}
        </div>
      )}
    </NavDashboard>
  )
}

export default DashboardRps
